import { useState } from 'react';

interface YouTubeProps {
  /** 영상 ID (`https://youtu.be/<id>` 의 뒷부분). */
  id: string;
  title: string;
  /** 카드에 적을 한 줄 설명. */
  description?: string;
  /** `12:20` 처럼 표기할 길이. */
  duration?: string;
}

/**
 * 긴 워크플로 영상. 섹션 끝에 카드로 두고, 본문 중간에 끼우지 않는다.
 *
 * iframe 을 그냥 박으면 카드 하나당 ~1MB JS 와 서드파티 쿠키가 붙어서 문서 페이지가
 * 느려진다. 클릭 전까지는 썸네일 이미지만 두고, 누른 뒤에야 iframe 을 꽂는다.
 * 도메인도 `youtube-nocookie.com` 을 쓴다.
 */
export function YouTube({ id, title, description, duration }: YouTubeProps) {
  const [activated, setActivated] = useState(false);

  if (activated) {
    return (
      <div className="my-6 aspect-video overflow-hidden rounded-xl border border-neutral-200 dark:border-white/12">
        <iframe
          className="h-full w-full"
          src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setActivated(true)}
      className="my-6 grid w-full grid-cols-[10.875rem_1fr] items-center gap-4 rounded-xl border border-neutral-200 bg-neutral-50/60 p-3 text-left transition hover:border-brand-300 dark:border-white/12 dark:bg-white/5 dark:hover:border-brand-500/40"
    >
      <span className="relative grid aspect-video place-items-center overflow-hidden rounded-lg bg-neutral-900">
        <img
          src={`https://i.ytimg.com/vi/${id}/mqdefault.jpg`}
          alt=""
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover opacity-80"
        />
        <span
          aria-hidden
          className="relative grid h-7 w-10 place-items-center rounded-lg bg-[#f03] text-[11px] text-white"
        >
          ▶
        </span>
        {duration && (
          <span className="absolute right-1.5 bottom-1.5 rounded bg-black/80 px-1.5 font-mono text-[10px] text-white">
            {duration}
          </span>
        )}
      </span>
      <span>
        <span className="block text-[14.5px] font-bold text-neutral-900 dark:text-neutral-100">
          {title}
        </span>
        {description && (
          <span className="mt-1 block text-[13px] leading-6 text-neutral-500 dark:text-neutral-400">
            {description}
          </span>
        )}
        <span className="mt-2 block text-[11.5px] text-neutral-400">
          ▶ YouTube · 클릭하면 이 자리에서 재생됩니다
        </span>
      </span>
    </button>
  );
}
